import React, { Fragment, memo, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Field, reduxForm } from 'redux-form';
import { Web5Provider, TwoFA } from '@Sage/ssm-web5';
import { Text, Loader } from '@Sage/hoi-poi-ui';

import { Core } from 'managers';
import { ADMIN_API_URL } from 'constants/Environment';
import { Alert } from 'components';
import Literal from 'components/literal';
import AppLoader from 'components/AppLoader';
import { t } from 'utils/getLiteral';
import { AuthenticationActions } from 'actions';
import PasswordField from './password-field';
import TextField from './text-field';
import { DarkSageLogo } from 'components/SvgIcons';
import { intercomHideAndShow } from 'utils/intercom';
import theme from 'utils/theme';
import { parseAuthHash, sanitizeEntityToGo } from 'utils/authCallback';

import './styles.scss';
import { externalAuthentication } from 'actions/Authentication';

const mapStateToProps = ({ authentication, literal }) => ({
    auth: authentication,
    locales: literal.literals,
});

const mapDispatchToProps = (dispatch) => ({
    login: bindActionCreators(AuthenticationActions, dispatch).login,
    loginInfo: bindActionCreators(AuthenticationActions, dispatch).loginInfo,
    close2FA: bindActionCreators(AuthenticationActions, dispatch).close2FA,
    finish2FA: bindActionCreators(AuthenticationActions, dispatch).finish2FA,
    oauthCallbackLogin: bindActionCreators(AuthenticationActions, dispatch).oauthCallbackLogin,
    loginToken: bindActionCreators(AuthenticationActions, dispatch).loginToken,
    resetUserLoginInfo: bindActionCreators(AuthenticationActions, dispatch).resetUserLoginInfo,
});

const errors = {
    login: 'helptext_password_wrong',
    permissions: 'cfm_helptext_login_no_permission',
    '2fa': 'error_login_failed',
    general: 'cfm_error_server_login',
    user_blocked: 'label_user_blocked',
};

function AuthenticationPage({
    auth = {},
    locales,
    handleSubmit = () => null,
    login = () => null,
    close2FA,
    finish2FA,
    loginInfo,
    oauthCallbackLogin,
    loginToken,
    resetUserLoginInfo,
}) {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const entityToGoRef = useRef(sanitizeEntityToGo(localStorage.getItem('entityToGo')));
    const authHash = useMemo(() => parseAuthHash(window.location.hash), []);

    let {
        request,
        oauthRequest,
        authenticated,
        canSwitchImpl,
        is2FAOpen,
        data2FA,
        userLoginInfo = {},
        error,
    } = auth;
    const { authenticationType, authenticationServer } = userLoginInfo;

    useEffect(() => {
        intercomHideAndShow('hide');

        const { webToken, sandboxToken, state, oauthUrl, entityToGo } = authHash;

        if (webToken) {
            if (entityToGo) {
                localStorage.setItem('entityToGo', entityToGo);
                entityToGoRef.current = entityToGo;
            }
            navigate('/auth/login');
            loginToken(webToken, true);
            return;
        }

        if (sandboxToken) {
            navigate('/auth/login');
            loginToken(sandboxToken);
            return;
        }

        if (state && oauthUrl) {
            navigate('/auth/login');
            oauthCallbackLogin(state, oauthUrl);
        }
        // Hash callback is intentionally handled once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!authenticated) {
            return;
        }

        if (canSwitchImpl && !authHash.sandboxToken) {
            navigate('/choose');
        } else if (entityToGoRef.current) {
            const destination = entityToGoRef.current;
            localStorage.removeItem('entityToGo');
            entityToGoRef.current = null;
            navigate(`/app/${destination}`);
        } else {
            navigate('/app/workspace');
        }
    }, [authenticated, canSwitchImpl, navigate, authHash]);

    const toggleShowPassword = useCallback(() => {
        setShowPassword(!showPassword);
    }, [showPassword]);

    useEffect(() => {
        if (authenticationType && authenticationType !== -1 && authenticationServer) {
            externalAuthentication(authenticationServer.address);
        }
    }, [authenticationType, authenticationServer]);

    const onClose = useCallback((error) => close2FA(error), [close2FA]);

    const onFinish = useCallback((data) => finish2FA(data), [finish2FA]);

    const renderError = useMemo(() => {
        if (authenticated === false && error !== null) {
            return (
                <Alert
                    type="danger"
                    text={t(errors[error.errorType || 'general'])}
                    dataTest="authentication-alert-error"
                />
            );
        }
        return '';
    }, [authenticated, error]);

    const web5Context = useMemo(() => {
        if (!data2FA.ready) return null;
        return {
            token: Core.cacheManager.getTokenStore(),
            apiBaseURL: ADMIN_API_URL,
            locales,
            platform: 107,
        };
    }, [locales, data2FA.ready]);

    return (
        <div className="container auth-container">
            {oauthRequest && !error && <AppLoader />}
            {!oauthRequest && (
                <Fragment>
                    <div className="form-header">
                        <div className="form-header__logo">
                            <DarkSageLogo />
                        </div>
                        <div className="form-header-title">
                            <Text type="h6" bold>
                                {t('cfm_title_login_access')}
                            </Text>
                        </div>
                    </div>
                    <div className="form-content">
                        {renderError}

                        <form
                            onSubmit={
                                authenticationType === -1
                                    ? handleSubmit((data) => login(data))
                                    : handleSubmit((data) => loginInfo(data))
                            }
                        >
                            <div className="form-group">
                                <Field
                                    label={t('cfm_label_login_user')}
                                    name="username"
                                    autoComplete="username"
                                    component={TextField}
                                    props={{
                                        dataTest: 'authentication-username',
                                        autoComplete: 'username',
                                    }}
                                    onFocus={resetUserLoginInfo}
                                />
                            </div>
                            {authenticationType === -1 && (
                                <Field
                                    label={t('cfm_label_login_pass')}
                                    name="password"
                                    component={PasswordField}
                                    showPassword={showPassword}
                                    toggleShowPassword={toggleShowPassword}
                                    props={{
                                        dataTest: 'authentication-password',
                                    }}
                                />
                            )}
                            <div className="form-submit">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={request === true}
                                    data-test="authentication-submit"
                                >
                                    {request ? (
                                        <Loader color="white" />
                                    ) : (
                                        <Literal literal="cfm_action_signin" />
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </Fragment>
            )}
            {data2FA.ready && (
                <Web5Provider context={web5Context} theme={theme}>
                    <TwoFA
                        isOpen={is2FAOpen}
                        onClose={onClose}
                        onFinish={onFinish}
                        isFirstTime={data2FA.isFirstTime}
                        isPhoneEditable={data2FA.isFirstTime || !data2FA.numOKAccess}
                        prefixId={data2FA.prefixId}
                        phoneNumber={data2FA.phoneNumber}
                        prefixList={data2FA.prefixList}
                        credentials={data2FA.credentials}
                    />
                </Web5Provider>
            )}
        </div>
    );
}

AuthenticationPage.propTypes = {
    auth: PropTypes.shape({
        authenticated: PropTypes.bool,
    }),
    login: PropTypes.func,
    handleSubmit: PropTypes.func,
    close2FA: PropTypes.func,
    finish2FA: PropTypes.func,
};

export default memo(
    reduxForm({
        form: 'AuthenticateForm',
        fields: ['username', 'password'],
    })(connect(mapStateToProps, mapDispatchToProps)(AuthenticationPage)),
);
